import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";

export interface OrgSettingsDocumentResponse {
  content: string;
  readme: string;
}

/**
 * Fetch/save/clear for one org's settings document on one surface — mirrors `useJsonSlot.ts`'s
 * shape (query + mutations + invalidate-on-success), against the org-scoped
 * `/config/organization/:systemDomainId/settings/:surface` routes instead of the global
 * `/config/json-slots/:slotKey` ones. `surface` is undefined while the picker has nothing
 * selected yet; the query key includes it so switching surfaces never shows the previous
 * surface's stale content.
 */
export function useOrgSettingsDocument(
  systemDomainId: string | undefined,
  surface: string | undefined
) {
  const queryClient = useQueryClient();
  const queryKey = ["orgSettings", surface, systemDomainId];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    enabled: !!systemDomainId && !!surface,
    queryFn: async () =>
      ApiService.fetch<OrgSettingsDocumentResponse>(
        "GET",
        `/api/config/organization/${systemDomainId}/settings/${surface}`
      ),
  });

  const saveMutation = useMutation({
    mutationFn: async (content: string) => {
      await ApiService.fetch(
        "PUT",
        `/api/config/organization/${systemDomainId}/settings/${surface}`,
        { content }
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "POST",
        `/api/config/organization/${systemDomainId}/settings/${surface}/clear`
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return {
    data,
    isLoading,
    isError,
    save: saveMutation.mutate,
    isSaving: saveMutation.isLoading,
    saveError: saveMutation.isError
      ? (saveMutation.error as Error)?.message || "Error saving settings"
      : undefined,
    clear: clearMutation.mutate,
    isClearing: clearMutation.isLoading,
    clearError: clearMutation.isError
      ? (clearMutation.error as Error)?.message || "Error clearing override"
      : undefined,
  };
}

export default useOrgSettingsDocument;
